'use client';

import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useTransition
} from 'react';

import { useRouter } from 'next/navigation';
import Countdown, { zeroPad, CountdownRenderProps } from 'react-countdown';

import { cn } from '@/lib/utils/cn';
import { GlobalDataContext } from '@/providers/GlobalDataProvider';

export interface I_CountdownProps {
  /** Target date/time for countdown (timestamp or Date object) */
  date: number | Date;

  /** Custom CSS classes */
  className?: string;

  /** Text to show when countdown completes */
  completedText?: string;

  /** Callback when countdown completes */
  onComplete?: () => void;

  /** Whether to show zero-padded numbers */
  zeroPad?: boolean;

  /** Whether to auto-start the countdown */
  autoStart?: boolean;

  /** Custom renderer function */
  renderer?: (props: CountdownRenderProps) => React.ReactNode;

  /** Threshold in ms to change color (e.g., 120000 for 2 mins) */
  criticalThreshold?: number;

  /** Tailwind class to apply when below threshold (default: 'text-destructive') */
  criticalColorClass?: string;

  /** Refresh the route on completion (global lock prevents duplicate refreshes) */
  refreshOnComplete?: boolean;
}

export function CountdownTimer({
  date,
  className,
  completedText = "Time's up!",
  onComplete,
  zeroPad: zeroPadNumbers = true,
  autoStart = true,
  renderer,
  criticalThreshold,
  criticalColorClass = 'text-destructive',
  refreshOnComplete = true
}: I_CountdownProps) {
  const router = useRouter();
  const { get, updateGlobalData } = React.useContext(GlobalDataContext);
  const [isPending, startTransition] = useTransition();
  const [isClient, setIsClient] = useState(false);
  const [mountedDate, setMountedDate] = useState<number | Date | null>(null);
  const [remountKey, setRemountKey] = useState(0);

  const normalizedDate = useMemo(() => {
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : date;
    }
    const numDate = typeof date === 'number' ? date : Date.parse(String(date));
    return isNaN(numDate) ? null : numDate;
  }, [date]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (normalizedDate) {
      setMountedDate(normalizedDate);
      setRemountKey(prev => prev + 1);
    }
  }, [normalizedDate]);

  useEffect(() => {
    if (normalizedDate && isClient) {
      const timer = setTimeout(() => {
        setRemountKey(prev => prev + 1);
        setMountedDate(normalizedDate);
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [isClient, normalizedDate]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && normalizedDate) {
        setRemountKey(prev => prev + 1);
        setMountedDate(normalizedDate);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [normalizedDate]);

  const defaultRenderer = useCallback(
    ({
      days: _days,
      hours,
      minutes,
      seconds,
      completed,
      total
    }: CountdownRenderProps) => {
      if (completed) {
        return (
          <span className={cn('font-bold', className)}>{completedText}</span>
        );
      }

      const pad = zeroPadNumbers ? zeroPad : (n: number) => n.toString();

      // Convert days to hours and show hours:minutes:seconds
      const totalHours = _days * 24 + hours;

      const isCritical =
        criticalThreshold !== undefined && total <= criticalThreshold;

      return (
        <span
          className={cn(
            'font-mono font-bold',
            className,
            isCritical && criticalColorClass,
            isCritical && 'animate-pulse'
          )}
        >
          {pad(totalHours)}:{pad(minutes)}:{pad(seconds)}
        </span>
      );
    },
    [
      className,
      completedText,
      zeroPadNumbers,
      criticalThreshold,
      criticalColorClass
    ]
  );

  const countdownRenderer = useMemo(
    () => renderer || defaultRenderer,
    [renderer, defaultRenderer]
  );

  const handleComplete = useCallback(() => {
    onComplete?.();
    if (refreshOnComplete && !get().isCountdownRefreshing) {
      updateGlobalData({ isCountdownRefreshing: true });
      startTransition(() => {
        router.refresh();
      });
    }
  }, [
    onComplete,
    refreshOnComplete,
    get,
    updateGlobalData,
    startTransition,
    router
  ]);

  // Force countdown remount after router.refresh() completes
  // This is crucial for iOS where the component state doesn't automatically reset after refresh
  useEffect(() => {
    if (get().isCountdownRefreshing && !isPending) {
      updateGlobalData({ isCountdownRefreshing: false });
      setRemountKey(prev => prev + 1);
      setMountedDate(normalizedDate);
    }
  }, [get, isPending, updateGlobalData, normalizedDate]);

  if (!isClient || !mountedDate) {
    return (
      <span className={cn('font-mono font-bold', className)}>--:--:--</span>
    );
  }

  const dateKey =
    typeof mountedDate === 'number'
      ? `${mountedDate}-${remountKey}`
      : `${mountedDate.getTime()}-${remountKey}`;

  return (
    <Countdown
      key={dateKey}
      date={mountedDate}
      renderer={countdownRenderer}
      onComplete={handleComplete}
      autoStart={autoStart}
    />
  );
}

export default CountdownTimer;
